import { i18n } from '@quadralize/i18n'
import { parseIsoDate, toIsoDate } from '@quadralize/utils'
import { CloseCircle } from '@solar-icons/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Combobox } from '../../forms/combobox/combobox'
import { DatePicker } from '../../forms/date-picker/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../forms/select/select'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../../overlays/drawer/drawer'
import { Button } from '../../ui/button/button'
import { clampIsoDate } from './data-table-date-filter.utils'
import type { DataTableFilterDrawerProps } from './data-table-filters.types'
import { getActiveFilterCount } from './data-table-filters.types'

const APPLY_FILTERS_AFTER_CLOSE_DELAY_MS = 240

/**
 * Generic DataTable filter drawer for mobile view
 * Displays filters in a bottom drawer
 */
export function DataTableFilterDrawer({
  filters,
  onClearFilters,
  title,
  description,
  trigger,
  hasSearch,
}: DataTableFilterDrawerProps & { hasSearch?: boolean }) {
  const { t } = useTranslation('ui', { i18n })
  const [open, setOpen] = useState(false)
  const [pendingFilters, setPendingFilters] = useState<Record<string, string>>({})
  const resolvedTitle = title ?? t('dataTable.filters.title')
  const getAllOptionLabel = (label: string) => {
    if (label === 'Status') return t('dataTable.filters.all')
    if (label === 'Esporte') return t('dataTable.filters.allSports')
    if (label === 'Categoria') return t('dataTable.filters.allCategories')
    if (label === 'Quadra') return 'Todas as quadras'
    if (label === 'Tipo') return 'Todos os tipos'
    return `Todos ${label}`
  }

  // Sync pending filters with actual filters when drawer opens
  useEffect(() => {
    if (open) {
      const initialFilters: Record<string, string> = {}
      filters.forEach((f) => {
        initialFilters[f.id] = String(f.value)
        if (f.type !== 'date') {
          const currentValue = String(f.value)
          f.onPendingValueChange?.(currentValue === 'all' ? 'all' : currentValue)
        }
      })
      setPendingFilters(initialFilters)
    }
  }, [open, filters])

  const activeFilterCount = getActiveFilterCount(filters)
  const hasActiveFilters = activeFilterCount > 0
  const hasPendingChanges = filters.some((filter) => {
    const pendingValue = pendingFilters[filter.id]
    return pendingValue !== undefined && pendingValue !== String(filter.value)
  })
  const showClearButton = hasActiveFilters || hasSearch

  const handleClearFilters = () => {
    onClearFilters()
    setOpen(false)
  }

  const handleApplyFilters = () => {
    const applyFilterCallbacks: Array<() => void> = []

    filters.forEach((filter) => {
      const pendingValue = pendingFilters[filter.id]
      if (pendingValue !== undefined && pendingValue !== String(filter.value)) {
        if (filter.type === 'date') {
          applyFilterCallbacks.push(() => {
            filter.onValueChange(pendingValue)
          })
          return
        }

        applyFilterCallbacks.push(() => {
          filter.onValueChange(pendingValue === 'all' ? 'all' : pendingValue)
        })
      }
    })

    setOpen(false)

    if (applyFilterCallbacks.length === 0) {
      return
    }

    window.setTimeout(() => {
      applyFilterCallbacks.forEach((applyFilter) => {
        applyFilter()
      })
    }, APPLY_FILTERS_AFTER_CLOSE_DELAY_MS)
  }

  const handleFilterChange = (filter: (typeof filters)[number], value: string) => {
    setPendingFilters((prev) => ({ ...prev, [filter.id]: value }))
    if (filter.type !== 'date') {
      filter.onPendingValueChange?.(value === 'all' ? 'all' : value)
    }
  }

  const applyFilterAfterClose = (applyFilter: () => void) => {
    setOpen(false)
    window.setTimeout(() => {
      applyFilter()
    }, APPLY_FILTERS_AFTER_CLOSE_DELAY_MS)
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    // Remove focus from trigger button when opening to prevent aria-hidden focus warning
    if (newOpen && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    if (!newOpen) {
      filters.forEach((filter) => {
        if (filter.type === 'date') return
        const currentValue = String(filter.value)
        filter.onPendingValueChange?.(currentValue === 'all' ? 'all' : currentValue)
      })
    }
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{resolvedTitle}</DrawerTitle>
          {description && <DrawerDescription>{description}</DrawerDescription>}
        </DrawerHeader>

        <div className="flex flex-col gap-4 p-4">
          {filters.map((filter) => (
            <div key={filter.id}>
              <label
                htmlFor={`drawer-${filter.id}`}
                className="text-sm font-medium text-text-primary mb-2 block"
              >
                {filter.label}
              </label>
              {filter.type === 'date' ? (
                <DatePicker
                  id={`drawer-${filter.id}`}
                  date={parseIsoDate(pendingFilters[filter.id] || filter.value)}
                  setDate={(date) =>
                    handleFilterChange(
                      filter,
                      clampIsoDate(toIsoDate(date), filter.min, filter.max),
                    )
                  }
                  placeholder={filter.placeholder}
                />
              ) : filter.searchable ? (
                <Combobox
                  id={`drawer-${filter.id}`}
                  value={
                    (pendingFilters[filter.id] || String(filter.value)) === 'all'
                      ? ''
                      : pendingFilters[filter.id] || String(filter.value)
                  }
                  onValueChange={(val) => {
                    const resolvedValue = val || 'all'

                    if (filter.immediateValues?.includes(resolvedValue)) {
                      filter.onValueChange(resolvedValue === 'all' ? 'all' : resolvedValue)
                      handleFilterChange(filter, resolvedValue)
                      return
                    }

                    if (filter.mobileImmediateValues?.includes(resolvedValue)) {
                      applyFilterAfterClose(() => {
                        filter.onValueChange(resolvedValue === 'all' ? 'all' : resolvedValue)
                      })
                      return
                    }

                    handleFilterChange(filter, resolvedValue)
                  }}
                  options={[
                    ...(filter.showAllOption !== false
                      ? [{ value: 'all', label: getAllOptionLabel(filter.label) }]
                      : []),
                    ...filter.options.map((option) => ({
                      value: String(option.value),
                      label: option.label,
                    })),
                  ]}
                  fetcher={
                    filter.fetchOptions
                      ? async (query) => {
                          const fetchedOptions = await filter.fetchOptions?.(query)
                          return [
                            ...(filter.showAllOption !== false
                              ? [{ value: 'all', label: getAllOptionLabel(filter.label) }]
                              : []),
                            ...(fetchedOptions ?? []).map((option) => ({
                              value: String(option.value),
                              label: option.label,
                            })),
                          ]
                        }
                      : undefined
                  }
                  placeholder={filter.placeholder}
                  searchPlaceholder={filter.searchPlaceholder ?? `Buscar ${filter.label}`}
                  emptyMessage={filter.emptyMessage ?? t('dataTable.filters.noResults')}
                  clearable
                />
              ) : (
                <Select
                  value={pendingFilters[filter.id] || String(filter.value)}
                  onValueChange={(val) => {
                    if (filter.immediateValues?.includes(val)) {
                      filter.onValueChange(val === 'all' ? 'all' : val)
                      handleFilterChange(filter, val)
                      return
                    }

                    if (filter.mobileImmediateValues?.includes(val)) {
                      applyFilterAfterClose(() => {
                        filter.onValueChange(val === 'all' ? 'all' : val)
                      })
                      return
                    }

                    handleFilterChange(filter, val)
                  }}
                >
                  <SelectTrigger id={`drawer-${filter.id}`}>
                    <SelectValue placeholder={filter.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {filter.showAllOption !== false && (
                      <SelectItem value="all">{getAllOptionLabel(filter.label)}</SelectItem>
                    )}
                    {filter.options.map((option) => (
                      <SelectItem key={String(option.value)} value={String(option.value)}>
                        {option.icon ? (
                          <div className="flex items-center gap-2">
                            {option.icon}
                            <span className="truncate">{option.label}</span>
                          </div>
                        ) : (
                          option.label
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>

        <DrawerFooter>
          {showClearButton && (
            <Button variant="outline" onClick={handleClearFilters} startIcon={<CloseCircle />}>
              {t('common.actions.clearFilters')}
            </Button>
          )}
          <Button
            variant={hasPendingChanges || hasActiveFilters ? 'secondary' : 'default'}
            onClick={handleApplyFilters}
          >
            {hasPendingChanges || hasActiveFilters
              ? t('dataTable.filters.applyFilters')
              : t('dataTable.filters.close')}
          </Button>
          {/* Removed DrawerClose because we handle closing manually in handleApplyFilters */}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
